import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Segment, ActiveSegment } from '@/types/segments';
import { triggerHaptic } from '@/lib/haptics';
import { ImpactStyle } from '@capacitor/haptics';
import { toast } from 'sonner';

const SEGMENT_ENTRY_RADIUS = 50; // meters
const SEGMENT_EXIT_RADIUS = 50; // meters

export const useSegmentDetection = (
  currentLat: number | null,
  currentLng: number | null,
  runId: string | null,
  userId: string | null
) => {
  // TODO: Implement segments table and feature
  // Temporarily disabled - table does not exist in database
  return {
    nearbySegments: [],
    activeSegment: null,
  };

  /* DISABLED UNTIL SEGMENTS TABLE IS CREATED
  const [nearbySegments, setNearbySegments] = useState<Segment[]>([]);
  const [activeSegment, setActiveSegment] = useState<ActiveSegment | null>(null);

  // Helper: Calculate distance between two GPS points
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Fetch nearby segments
  useEffect(() => {
    if (!currentLat || !currentLng) return;

    const fetchNearbySegments = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from('segments')
          .select('*')
          .limit(20);

        if (error) throw error;

        // Filter by distance client-side
        const nearby = (data || []).filter((segment: any) => {
          const distToStart = calculateDistance(
            currentLat,
            currentLng,
            segment.start_lat,
            segment.start_lng
          );
          return distToStart < 1000; // Within 1km
        });

        setNearbySegments(nearby as Segment[]);
      } catch (error) {
        console.error('Error fetching segments:', error);
      }
    };

    fetchNearbySegments();
    const interval = setInterval(fetchNearbySegments, 30000); // Refresh every 30s

    return () => clearInterval(interval);
  }, [currentLat, currentLng]);

  // Detect segment entry/exit
  useEffect(() => {
    if (!currentLat || !currentLng || !runId || !userId) return;

    nearbySegments.forEach((segment) => {
      const distToStart = calculateDistance(
        currentLat,
        currentLng,
        segment.start_lat,
        segment.start_lng
      );

      // Entering segment
      if (distToStart < SEGMENT_ENTRY_RADIUS && !activeSegment) {
        const active: ActiveSegment = {
          ...segment,
          startedAt: Date.now(),
          enteredAt: new Date(),
        };
        setActiveSegment(active);
        triggerHaptic(ImpactStyle.Medium);
        toast.success(`Entrando no segmento: ${segment.name} (${(segment.distance_m / 1000).toFixed(1)}km)`);
        return;
      }

      // Exiting segment (reached end point)
      if (activeSegment && activeSegment.id === segment.id) {
        const distToEnd = calculateDistance(
          currentLat,
          currentLng,
          segment.end_lat,
          segment.end_lng
        );

        if (distToEnd < SEGMENT_EXIT_RADIUS) {
          const elapsedMs = Date.now() - activeSegment.startedAt;
          const elapsedSeconds = Math.floor(elapsedMs / 1000);
          const avgPace = (elapsedSeconds / 60) / (segment.distance_m / 1000);

          // Save effort
          saveSegmentEffort(segment.id, elapsedSeconds, avgPace);

          triggerHaptic(ImpactStyle.Heavy);
          toast.success(`Segmento concluído! Tempo: ${Math.floor(elapsedSeconds / 60)}:${(elapsedSeconds % 60).toString().padStart(2, '0')}`);
          setActiveSegment(null);
        }
      }
    });
  }, [currentLat, currentLng, nearbySegments, activeSegment, runId, userId]);

  const saveSegmentEffort = async (segmentId: string, elapsedSeconds: number, avgPace: number) => {
    if (!runId || !userId) return;

    try {
      await (supabase as any)
        .from('segment_efforts')
        .insert({
          segment_id: segmentId,
          user_id: userId,
          run_id: runId,
          elapsed_time_seconds: elapsedSeconds,
          avg_pace_min_km: avgPace,
        });
    } catch (error) {
      console.error('Error saving segment effort:', error);
    }
  };

  return {
    nearbySegments,
    activeSegment,
  };
  */
};
